import useApiHook from "@hooks/useApiHook";
import React, { useCallback, useEffect, useState } from "react";
import useLoader from "@hooks/useLoader";
import TransparentLoader from "@components/Loaders/TransparentLoader";
import useApiError from "@hooks/useApiError";
import useNotification from "@src/hooks/useNotification";
import { emailRegex } from "@src/utils/Regex";
import inputBgMd from "@assets/images/backgrounds/inputBgMd.webp";
import useIsMobile from "@src/hooks/useIsMobile";
import { useSearchParams } from "react-router-dom";
import type { IForgotPassword } from "@src/types/AppTypes";

const ForgotPassowrd = React.memo(
  React.forwardRef<HTMLDivElement, IForgotPassword>(
    ({ forgotModal, modalBorder, setLoginModal, setForgotModal }, ref) => {
      const [searchParams, setSearchParams] = useSearchParams();
      const isMobile = useIsMobile();
      const setApiError = useApiError();
      const { isLoading, loaderDispatch } = useLoader();
      const recoveryEndpoint = useApiHook({
        url: "/users/passwords/recovery",
        method: "post",
      });
      const [email, setEmail] = useState<string>("");
      const flashNotification = useNotification();
      const [error, setError] = useState<{
        type: "email";
        value: string;
      } | null>(null);

      const submit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>, email: string) => {
          e.preventDefault();
          if (!email.trim().length) {
            setError({
              type: "email",
              value: "Please enter an email address.",
            });
            document
              .getElementById("signupEmail")
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
          if (!emailRegex.test(email)) {
            setError({
              type: "email",
              value: "Please enter a valid email address.",
            });
            document
              .getElementById("signupEmail")
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
          try {
            loaderDispatch({ type: "update", payload: true });
            const res = await recoveryEndpoint<{
              status: boolean;
              message: string;
            }>({
              user: { email },
            });
            if (res && res.data.status === true) {
              flashNotification({ ...res.data, timer: 100000 });
              if (!isMobile) {
                setForgotModal(false);
              } else {
                searchParams.delete("tab");
                setSearchParams(searchParams, { replace: true });
              }
            }
            if (res && res.data.status === false) {
              flashNotification(res.data);
            }
          } catch (e: any) {
            setApiError(e);
          } finally {
            loaderDispatch({ type: "update", payload: false });
          }
        },
        [
          isMobile,
          searchParams,
          setSearchParams,
          loaderDispatch,
          setApiError,
          setForgotModal,
          recoveryEndpoint,
          flashNotification,
        ]
      );

      useEffect(() => {
        const header = document.getElementById("header");
        const footer = document.getElementById("footer");

        document.body.style.overflow = "hidden";

        if (header) {
          header.style.zIndex = "0";
        }
        if (footer) {
          footer.style.zIndex = "-1";
        }

        return () => {
          // Enable background scroll
          document.body.style.overflow = "auto";

          if (header) {
            header.style.zIndex = "";
          }
          if (footer) {
            footer.style.zIndex = "";
          }
        };
      }, []);

      return (
        <div
          className={`fixed size-full bg-[#00000080] top-0 left-0 grid place-items-center z-[9999] overflow-auto modal-content ${
            forgotModal ? "block" : "hidden"
          }`}
        >
          <div
            ref={ref}
            id="forgot-password-form"
            onAnimationEnd={() => {
              document
                .getElementById("forgot-password-form")
                ?.classList.remove("form-shake");
            }}
            className="max-w-2xl max-md:max-w-[95%] max-md:mx-auto w-full bg-gradient-to-b from-[#1D1159] to-[#00AEEF] relative rounded-[5px_30px_40px_0px] forgot-password-form"
          >
            <img
              src={modalBorder}
              className="absolute top-0 left-0 size-full z-[0]"
              alt=""
            />
            <div className="relative z-10 p-16 max-md:p-8">
              <button
                onClick={() => {
                  setLoginModal({ type: "updateLogin", payload: false });
                  setForgotModal(!forgotModal);
                  if (isMobile) {
                    searchParams.delete("tab");
                    setSearchParams(searchParams, { replace: true });
                  }
                }}
                className="text-primary absolute top-10 max-[512px]:top-5 right-10 max-[512px]:right-5 text-3xl max-md:text-2xl ml-auto block duration-150 hover:scale-95"
                aria-label="Close"
              >
                <i className="ri-close-circle-line"></i>
              </button>
              <h1 className="text-primary text-3xl max-md:text-2xl">
                Recover Password
              </h1>
              <form
                onSubmit={(e) => submit(e, email)}
                className="my-10 max-sm:my-1"
              >
                {isLoading && <TransparentLoader />}
                <div className="mb-10 max-sm:mb-6">
                  <label
                    className="block text-secondary text-base f-family-mochiy-pop mb-2"
                    htmlFor="recoveryEmail"
                  >
                    Email
                  </label>
                  <div className="relative">
                    <input
                      autoComplete="email"
                      id="recoveryEmail"
                      className="relative text-black z-10 text-base w-full f-family-mochiy-pop bg-transparent focus:outline-none focus:ring-1 focus:ring-transparent focus:border-transparent bg-primary z- p-[1.6rem] pr-12 max-md:pr-12 max-md:px-4 max-md:py-5 rounded-[5px_27px_19px_25px] bg-100 f-family-mochiy-pop"
                      type="text"
                      placeholder="Enter email address"
                      value={email}
                      autoCapitalize="none"
                      inputMode="text"
                      onChange={(e) => {
                        setEmail(e.target.value), setError(null);
                      }}
                    />
                    <img
                      className="absolute top-0 left-0 size-full"
                      src={inputBgMd}
                      alt=""
                    />
                  </div>
                  <p className="text-red-500 mt-1 text-sm f-family-mochiy-pop w-full">
                    {error?.type === "email" && error.value}
                  </p>
                </div>
                <div className="px-7 max-sm:px-0">
                  <button
                    disabled={isLoading || !email.trim().length}
                    type="submit"
                    className="w-full text-2xl max-md:text-xl customized-button rounded-[5px_20px_11px_20px] px-10 max-md:px-5 disabled:hover:scale-100"
                    aria-label="Submit"
                  >
                    Submit
                  </button>
                </div>
              </form>
              <h5 className="text-lg max-sm:text-base text-center block text-white">
                Shouldn't be here?
                <button
                  className="inline-block duration-150 text-[#1D1159] hover:text-primary max-sm:hover:text-[#1D1159] ms-2"
                  onClick={() => {
                    setForgotModal(false);
                    setLoginModal({ type: "updateLogin", payload: true });
                    if (isMobile) {
                      searchParams.set("tab", "login");
                      setSearchParams(searchParams, { replace: true });
                    }
                  }}
                  aria-label="Login"
                >
                  Login
                </button>
              </h5>
            </div>
          </div>
        </div>
      );
    }
  )
);

export default ForgotPassowrd;
